import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ShieldAlert, AlertTriangle } from 'lucide-react';

export interface AllergyWarning {
  productId: string | null;
  productDescription: string;
  matchedAllergy: string;
  productAllergyTag: string;
}

interface AllergyWarningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  warnings: AllergyWarning[];
  patientName?: string;
  patientAllergies?: string[];
  onAcknowledge: () => void;
  onGoBack: () => void;
}

const AllergyWarningDialog: React.FC<AllergyWarningDialogProps> = ({
  open,
  onOpenChange,
  warnings,
  patientName,
  patientAllergies,
  onAcknowledge,
  onGoBack,
}) => {
  const [acknowledged, setAcknowledged] = useState(false);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setAcknowledged(false);
    }
    onOpenChange(newOpen);
  };

  // Group warnings by medication
  const groupedWarnings = warnings.reduce<Record<string, AllergyWarning[]>>((acc, w) => {
    const key = w.productDescription;
    if (!acc[key]) acc[key] = [];
    acc[key].push(w);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-[540px]'>
        <DialogHeader>
          <div className='flex items-center gap-3'>
            <div className='flex h-10 w-10 items-center justify-center rounded-full bg-red-100'>
              <ShieldAlert className='h-5 w-5 text-red-600' />
            </div>
            <div>
              <DialogTitle className='text-red-900'>Allergy Interaction Warning</DialogTitle>
              <p className='mt-0.5 text-sm text-red-600'>
                Potential allergy conflict detected
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className='space-y-4'>
          {/* Patient Allergy Summary */}
          {patientName && (
            <div className='rounded-lg border border-red-200 bg-red-50 p-3'>
              <p className='text-xs font-semibold text-red-800'>
                Patient: {patientName}
              </p>
              {patientAllergies && patientAllergies.length > 0 && (
                <div className='mt-2 flex flex-wrap gap-1.5'>
                  {patientAllergies.map((allergy, idx) => (
                    <Badge
                      key={idx}
                      className='bg-red-100 text-red-700 hover:bg-red-100 border-0 text-xs'
                    >
                      {allergy}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Conflicting Medications */}
          <div className='space-y-3'>
            <p className='text-sm font-medium text-slate-700'>
              The following medications may trigger an allergic reaction:
            </p>
            {Object.entries(groupedWarnings).map(([medication, medWarnings]) => (
              <div
                key={medication}
                className='rounded-lg border border-amber-200 bg-amber-50 p-3'
              >
                <div className='flex items-start gap-2'>
                  <AlertTriangle className='mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600' />
                  <div>
                    <p className='text-sm font-semibold text-amber-900'>{medication}</p>
                    <div className='mt-1 space-y-0.5'>
                      {medWarnings.map((w, idx) => (
                        <p key={idx} className='text-xs text-amber-700'>
                          Matches patient allergy: <strong>{w.matchedAllergy}</strong>
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Acknowledgement Checkbox */}
          <div className='flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3'>
            <Checkbox
              id='allergy-acknowledge'
              checked={acknowledged}
              onCheckedChange={(checked) => setAcknowledged(checked === true)}
              className='mt-0.5'
            />
            <label
              htmlFor='allergy-acknowledge'
              className='cursor-pointer text-sm leading-snug text-slate-700'
            >
              I have reviewed the patient's allergy history and understand the potential risk.
              I wish to proceed with this prescription.
            </label>
          </div>
        </div>

        <DialogFooter className='gap-2 sm:gap-0'>
          <Button
            variant='outline'
            onClick={() => {
              setAcknowledged(false);
              onGoBack();
            }}
          >
            Go Back
          </Button>
          <Button
            variant='destructive'
            onClick={() => {
              setAcknowledged(false);
              onAcknowledge();
            }}
            disabled={!acknowledged}
          >
            Proceed Anyway
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AllergyWarningDialog;
